import { useDownloadStore } from '@/stores/downloadStore'
import { extractErrorMessage } from '@/utils/errorHandler'
import { isUserCanceledError, withTransferLock } from '@/utils/transferLock'
import { toast } from 'sonner'
import streamSaver from 'streamsaver'

streamSaver.mitm = '/streamsaver/mitm.html'

export interface DownloadProgress {
	bytesReceived: number
	totalBytes: number
	percent: number
	speed: number // bytes per second
	eta: number // seconds remaining
}

export type ProgressCallback = (progress: DownloadProgress) => void

interface WindowWithHelpers {
	showSaveFilePicker?: (options?: { suggestedName?: string }) => Promise<{
		createWritable: () => Promise<WritableStream>
	}>
}

const getWindow = (): WindowWithHelpers => {
	return window as unknown as WindowWithHelpers
}

/**
 * A beautiful, real-time SVG Progress Circle component for Sonner toast icons.
 */
export const ProgressCircle = ({ percent }: { percent: number }) => {
	const radius = 8
	const circumference = 2 * Math.PI * radius
	const strokeDashoffset = circumference - (percent / 100) * circumference
	return (
		<svg width='20' height='20' viewBox='0 0 20 20' className='-rotate-90 select-none'>
			<title>Download progress: {percent}%</title>
			<circle cx='10' cy='10' r={radius} fill='transparent' stroke='var(--border)' strokeWidth='2' />
			<circle
				cx='10'
				cy='10'
				r={radius}
				fill='transparent'
				stroke='var(--primary)'
				strokeWidth='2'
				strokeDasharray={circumference}
				strokeDashoffset={strokeDashoffset}
				strokeLinecap='round'
				style={{ transition: 'stroke-dashoffset 0.3s ease-out' }}
			/>
		</svg>
	)
}

/**
 * Downloads a file from a response stream, writing it chunk-by-chunk to the disk.
 * Uses the File System Access API (showSaveFilePicker) if available,
 * falling back to StreamSaver.js.
 *
 * @param responseInput A promise that resolves to the Response object or a callback taking an AbortSignal.
 * @param filename The default name for the downloaded file.
 * @param onProgress Optional callback to receive progress updates.
 * @param showProgress Whether to show progress details (ETA, percent, speed) in the DownloadManager.
 * @param maxSizeLimit Optional cap on bytes downloaded.
 * @param useTransferLock When true, serializes this download against other locked firmware/file transfers
 * (see transferLock.ts) so it never runs at the same time as one of them and starves its bandwidth.
 */
export const downloadFileWithStreaming = async (
	responseInput: Promise<Response> | ((signal: AbortSignal) => Promise<Response>),
	filename: string,
	onProgress?: ProgressCallback,
	showProgress = true,
	maxSizeLimit?: number,
	useTransferLock = false,
): Promise<void> => {
	const downloadId = Math.random().toString(36).slice(2, 11)
	const abortController = new AbortController()
	const signal = abortController.signal
	let isDownloadAdded = false

	let writableStream: WritableStream | null = null
	const win = getWindow()
	const addToManager = true
	const limit = maxSizeLimit

	try {
		// 1. Prepare writable stream first if File System Access API is available
		if (win.showSaveFilePicker) {
			try {
				const fileHandle = await win.showSaveFilePicker({
					suggestedName: filename,
				})
				writableStream = await fileHandle.createWritable()
			} catch (err) {
				// User cancelled the file picker dialog
				const isCancel = err instanceof DOMException && err.name === 'AbortError'
				if (!isCancel) {
					console.error('File picker error:', err)
					toast.error('Failed to configure target file location')
				}
				return
			}
		}

		// 2. We successfully selected the file location (or skipping file picker for fallback).
		// Add to download manager immediately!
		if (addToManager) {
			useDownloadStore.getState().addDownload({
				id: downloadId,
				filename,
				abortController,
				showProgress,
			})
			isDownloadAdded = true
		}

		// 3. Define the background task that fetches the response headers and streams the file.
		// This runs completely in the background so that downloadFileWithStreaming can return immediately.
		const runBackgroundDownload = async (activeStream: WritableStream | null) => {
			let writer: WritableStreamDefaultWriter | null = null
			let finalStream = activeStream

			try {
				// Fetch response headers (this can take a few seconds)
				const response = await (typeof responseInput === 'function' ? responseInput(signal) : responseInput)
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`)
				}

				const body = response.body
				if (!body) {
					throw new Error('Response body is null or undefined')
				}

				const contentLength = Number(response.headers.get('Content-Length') || 0)
				const limitLabel = limit ? `${(limit / (1024 * 1024 * 1024)).toFixed(0)}GB` : ''

				// Limit check: Warn upfront if Content-Length exceeds the limit (download will be capped)
				if (limit && contentLength > limit) {
					toast.warning(`File size exceeds ${limitLabel}`, {
						description: (
							<span className='text-muted-foreground text-xs font-normal'>
								Only the first {limitLabel} of data will be downloaded.
							</span>
						),
					})
				}

				const reader = body.getReader()

				// If we don't have a writable stream yet (legacy fallback), initialize StreamSaver now
				if (!finalStream) {
					finalStream = streamSaver.createWriteStream(filename, {
						size: contentLength || undefined,
					})
				}

				if (addToManager) {
					useDownloadStore.getState().updateDownload(downloadId, {
						status: 'downloading',
						totalBytes: limit && contentLength > limit ? limit : contentLength,
					})
				}

				writer = finalStream.getWriter()
				let bytesReceived = 0
				let lastUpdateTime = Date.now()
				let lastUpdateBytes = 0

				// Read & write loop — await writer.ready before each write
				// to honour backpressure from the underlying file sink.
				while (true) {
					if (signal.aborted) {
						throw new DOMException('Aborted', 'AbortError')
					}

					const { done, value } = await reader.read()
					if (done) {
						break
					}

					if (value) {
						// Check if writing this chunk would exceed the limit
						if (limit && bytesReceived + value.length > limit) {
							const remaining = limit - bytesReceived
							if (remaining > 0) {
								await writer.ready
								await writer.write(value.slice(0, remaining))
								bytesReceived += remaining
							}
							reader.cancel().catch(() => {})
							toast.info(`Download capped at ${limitLabel}`, {
								description: (
									<span className='text-muted-foreground text-xs font-normal'>
										The first {limitLabel} of data has been saved successfully.
									</span>
								),
							})
							break
						}

						await writer.ready
						await writer.write(value)
						bytesReceived += value.length

						const now = Date.now()
						if (now - lastUpdateTime >= 500) {
							const intervalElapsed = (now - lastUpdateTime) / 1000
							const intervalBytes = bytesReceived - lastUpdateBytes
							const speed = intervalBytes / intervalElapsed

							const effectiveTotal = limit && contentLength > limit ? limit : contentLength
							const remainingBytes = effectiveTotal - bytesReceived
							const eta = effectiveTotal > 0 && speed > 0 ? remainingBytes / speed : Number.POSITIVE_INFINITY
							const percent = effectiveTotal > 0 ? Math.round((bytesReceived / effectiveTotal) * 100) : 0

							const progress: DownloadProgress = {
								bytesReceived,
								totalBytes: effectiveTotal,
								percent,
								speed,
								eta,
							}

							if (addToManager) {
								useDownloadStore.getState().updateDownload(downloadId, {
									status: 'downloading',
									bytesReceived,
									totalBytes: effectiveTotal,
									percent,
									speed,
									eta,
								})
							}

							if (onProgress) {
								onProgress(progress)
							}

							lastUpdateTime = now
							lastUpdateBytes = bytesReceived
						}
					}
				}

				// Close streams
				await writer.close()
				writer = null

				if (addToManager) {
					useDownloadStore.getState().updateDownload(downloadId, {
						status: 'completed',
						percent: 100,
						bytesReceived,
						totalBytes: bytesReceived,
						speed: 0,
						eta: 0,
					})
				}
			} catch (error) {
				if (writer) {
					try {
						await writer.abort()
					} catch {}
				}
				// Since we are running in the background, handle cancellations and failures here
				if (isDownloadAdded) {
					const isAborted = signal.aborted || (error instanceof DOMException && error.name === 'AbortError')
					if (isAborted) {
						useDownloadStore.getState().updateDownload(downloadId, {
							status: 'cancelled',
						})
					} else {
						console.error('Streaming download error:', error)
						const message = extractErrorMessage(error, 'Unknown error')
						useDownloadStore.getState().updateDownload(downloadId, {
							status: 'failed',
							error: message,
						})
						toast.error('Download failed', {
							description: <span className='text-muted-foreground text-xs font-normal'>{message}</span>,
						})
					}
				} else {
					// Even if not added to manager (silent download in fallback), show a toast error for visibility
					const isAborted = signal.aborted || (error instanceof DOMException && error.name === 'AbortError')
					if (!isAborted) {
						console.error('Streaming download error:', error)
						const message = extractErrorMessage(error, 'Unknown error')
						toast.error('Download failed', {
							description: <span className='text-muted-foreground text-xs font-normal'>{message}</span>,
						})
					}
				}
			}
		}

		// Execute the streaming loop in the background and return immediately
		if (useTransferLock) {
			withTransferLock(() => runBackgroundDownload(writableStream), {
				signal,
				onWaiting: () =>
					toast.info(`Waiting for another firmware/file transfer to finish before downloading "${filename}"...`),
			}).catch(error => {
				// runBackgroundDownload handles its own errors internally and never rejects — this only fires
				// if the download was cancelled while still queued, waiting for the lock.
				if (isUserCanceledError(error)) {
					if (isDownloadAdded) {
						useDownloadStore.getState().updateDownload(downloadId, { status: 'cancelled' })
					}
				} else {
					console.error('Transfer lock error:', error)
				}
			})
		} else {
			runBackgroundDownload(writableStream)
		}
	} catch (error: unknown) {
		const isAborted = signal.aborted || (error instanceof DOMException && error.name === 'AbortError')
		if (!isAborted) {
			console.error('Streaming download initialization error:', error)
			const message = extractErrorMessage(error, 'Unknown error')
			toast.error('Download failed to start', {
				description: <span className='text-muted-foreground text-xs font-normal'>{message}</span>,
			})
			throw error
		}
	}
}
